import { useEffect, useState } from "react";
import api from "../../lib/api";
import { StatCard, EmptyState, MODULE_LABELS } from "../../components/shared";
import { WithdrawCard } from "../../components/WithdrawCard";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Download, Lock } from "lucide-react";
import { Link } from "react-router-dom";

export default function Sales() {
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    api.get("/sales/summary").then((r) => setSummary(r.data));
    api.get("/sales/timeseries").then((r) => setSeries(r.data));
    api.get("/sales/breakdown").then((r) => setBreakdown(r.data));
    api.get("/payouts/my").then((r) => setPayouts(r.data));
    api.get("/membership/my").then((r) => setMembership(r.data));
  }, []);

  if (!summary) return <p className="text-neutral-500">Loading...</p>;

  const empty = summary.lifetime_units === 0 && summary.lifetime_royalty === 0;
  const hasAnalytics = !membership || membership.status === "none" || (membership.modules || []).includes("sales_analytics");

  return (
    <div data-testid="sales-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading">Sales & Royalties</h1>
          <p className="text-neutral-500 mt-1">Imported from Amazon sales reports by our team</p>
        </div>
        <button data-testid="download-statement-button"
          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sales/statement`, "_blank")}
          className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">
          <Download size={15} /> Download statement
        </button>
      </div>

      {empty ? (
        <EmptyState
          title="No sales data yet"
          subtitle="Once your book is live and sales reports are imported, your units, royalties and earnings will appear here."
          action={<Link to="/dashboard" className="text-neutral-900 font-medium text-sm hover:underline">Back to My Books</Link>}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard testId="stat-lifetime-units" label="Lifetime units" value={summary.lifetime_units.toLocaleString()} />
            <StatCard testId="stat-lifetime-earnings" label="Your lifetime earnings" value={`$${summary.lifetime_author_earning.toLocaleString()}`} sub={`of $${summary.lifetime_royalty.toLocaleString()} gross royalty`} />
            <StatCard testId="stat-paid-out" label="Paid out to you" value={`$${(payouts?.total_disbursed ?? 0).toLocaleString()}`} />
            <StatCard testId="stat-outstanding" label="Outstanding balance" value={`$${(payouts?.outstanding ?? 0).toLocaleString()}`} sub={`This month: $${summary.current_period_earning.toLocaleString()} · ${summary.current_period_units} units`} />
          </div>

          <WithdrawCard outstanding={payouts?.outstanding ?? 0} onChanged={() => api.get("/payouts/my").then((r) => setPayouts(r.data))} />

          {!hasAnalytics ? (
            <div data-testid="analytics-locked" className="bg-white border border-dashed border-neutral-300 rounded-lg p-10 text-center mb-8">
              <Lock size={22} className="mx-auto text-neutral-400 mb-3" />
              <h3 className="font-semibold font-heading text-neutral-900">Advanced sales analytics is a Professional & Premium module</h3>
              <p className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">
                Earnings-over-time charts, monthly unit trends and breakdowns by format, marketplace and book are included with the Professional and Premium plans. Your summary totals and statements remain available on the {membership?.plan_name || "current"} plan.
              </p>
            </div>
          ) : (
          <>
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="font-semibold font-heading mb-4">Earnings over time</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="author_earning" name="Your earnings ($)" stroke="#171717" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="royalty" name="Gross royalty ($)" stroke="#2563eb" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="font-semibold font-heading mb-4">Units sold per month</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="units" name="Units" fill="#171717" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="font-semibold font-heading mb-4">By format</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={breakdown?.by_format || []} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={90} />
                  <Tooltip />
                  <Bar dataKey="royalty" name="Royalty ($)" fill="#2563eb" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="font-semibold font-heading mb-4">By marketplace</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={breakdown?.by_marketplace || []} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={110} />
                  <Tooltip />
                  <Bar dataKey="royalty" name="Royalty ($)" fill="#059669" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <h3 className="font-semibold font-heading p-6 pb-0">By book</h3>
            <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left mt-4">
              <thead>
                <tr className="bg-neutral-50">
                  {["Title", "Units", "Gross royalty", "Your earnings"].map((h) => (
                    <th key={h} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {(breakdown?.by_book || []).map((b) => (
                  <tr key={b.project_id}>
                    <td className="px-6 py-3 font-medium text-neutral-900">{b.title || "—"}</td>
                    <td className="px-6 py-3">{b.units}</td>
                    <td className="px-6 py-3">${b.royalty.toFixed(2)}</td>
                    <td className="px-6 py-3 font-semibold text-emerald-700">${b.author_earning.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
          </>
          )}

          {payouts?.payouts?.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden mt-8" data-testid="author-payouts-table">
              <h3 className="font-semibold font-heading p-6 pb-0">Payout history</h3>
              <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left mt-4">
                <thead><tr className="bg-neutral-50">
                  {["Date", "Amount", "Method", "Note"].map((h) => (
                    <th key={h} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
                </tr></thead>
                <tbody className="divide-y divide-neutral-100">
                  {payouts.payouts.map((p) => (
                    <tr key={p.id}>
                      <td className="px-6 py-3">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="px-6 py-3 font-semibold text-emerald-700">${p.amount.toFixed(2)}</td>
                      <td className="px-6 py-3">{p.method}</td>
                      <td className="px-6 py-3 text-neutral-500">{p.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
